import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';
import { formationGuidanceEqual, prepareFormationGuidance } from '@waedo/frontend-core/formation-guidance';
import { getHockeyPositionLeaves } from '@waedo/frontend-core/hockey-positions';

type UnknownRecord = Record<string, unknown>;
interface FormationValidationIssue { path: Array<string | number>; code: string }

export class FormationApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly errors: FormationValidationIssue[] = []) { super(message); }
}

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const errorValue = (payload as UnknownRecord).error;
    if (typeof errorValue === 'string' && errorValue.trim().length > 0) return errorValue;
    if (errorValue && typeof errorValue === 'object' && 'message' in (errorValue as UnknownRecord)) {
      const message = (errorValue as UnknownRecord).message;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const formationsRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const url = `${baseUrl}${path}`;
  const response = await authenticatedFetch(url, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    const detail = payload && typeof payload === 'object' ? (payload as UnknownRecord).detail : undefined;
    const code = detail && typeof detail === 'object' ? (detail as UnknownRecord).code : undefined;
    if (typeof code === 'string' && ['FORMATION_POSITION_VALIDATION_FAILED', 'FORMATION_GUIDE_INVALID',
      'FORMATION_REVISION_REQUIRED', 'FORMATION_REVISION_CONFLICT', 'FORMATION_GUIDE_STORAGE_UNAVAILABLE'].includes(code)) {
      const errors = (detail as UnknownRecord).errors;
      throw new FormationApiError(code, response.status, code, Array.isArray(errors) ? errors
        .filter(item => item && typeof item.code === 'string' && Array.isArray(item.path))
        .map(item => ({ code: item.code, path: item.path.filter((part: unknown) => typeof part === 'string' || Number.isInteger(part)) })) : []);
    }
    throw new FormationApiError(extractMessage(payload, `Formations API request failed (${response.status})`), response.status);
  }

  return extractData(payload);
};

const guidanceWritePayload = (payload: Record<string, unknown>): Record<string, unknown> => {
  if (payload.guide === undefined) return payload;
  const positionKeys = payload.positions === undefined ? undefined : getHockeyPositionLeaves(payload.positions).map(leaf => leaf.slotKey);
  return { ...payload, guide: prepareFormationGuidance(payload.guide, { positionKeys }) };
};

/** Older servers may ignore unknown request fields: never report guidance saved without its own confirmed echo. */
const verifyGuidanceSaved = (response: unknown, payload: Record<string, unknown>, update: boolean): void => {
  if (payload.guide === undefined) return;
  const unverified = () => { throw new FormationApiError('FORMATION_GUIDE_SAVE_UNVERIFIED', 200, 'FORMATION_GUIDE_SAVE_UNVERIFIED'); };
  if (!response || typeof response !== 'object' || Array.isArray(response)) return unverified();
  const result = response as UnknownRecord;
  if (!Object.prototype.hasOwnProperty.call(result, 'guide') || !Object.prototype.hasOwnProperty.call(result, 'revision')
    || typeof result.revision !== 'number' || !Number.isSafeInteger(result.revision) || result.revision < 0) return unverified();
  if (update && result.revision !== Number(payload.expected_revision) + 1) return unverified();
  let stored: unknown;
  try { stored = prepareFormationGuidance(result.guide); } catch { return unverified(); }
  if (!formationGuidanceEqual(payload.guide, stored)) return unverified();
};

export const listFormationsApi = async (params?: {
  published_only?: boolean;
  match_type?: string;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (typeof params?.published_only === 'boolean') query.set('published_only', String(params.published_only));
  if (params?.match_type) query.set('match_type', params.match_type);
  const qs = query.toString();
  const payload = await formationsRequest(`/api/v1/formations${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return Array.isArray(payload) ? payload : [];
};

export const getFormationApi = async (
  keyOrId: string,
  params?: { published_only?: boolean }
): Promise<unknown | null> => {
  const query = new URLSearchParams();
  if (typeof params?.published_only === 'boolean') query.set('published_only', String(params.published_only));
  const qs = query.toString();
  const payload = await formationsRequest(
    `/api/v1/formations/${encodeURIComponent(keyOrId)}${qs ? `?${qs}` : ''}`,
    { method: 'GET' }
  );
  return payload ?? null;
};

export const createFormationApi = async (payload: Record<string, unknown>): Promise<unknown | null> => {
  const intended = guidanceWritePayload(payload);
  const response = await formationsRequest('/api/v1/formations', {
    method: 'POST',
    body: JSON.stringify(intended),
  });
  verifyGuidanceSaved(response, intended, false);
  return response ?? null;
};

export const updateFormationApi = async (
  idOrKey: string,
  payload: Record<string, unknown>
): Promise<unknown | null> => {
  const intended = guidanceWritePayload(payload);
  if (intended.guide !== undefined && (typeof intended.expected_revision !== 'number'
    || !Number.isSafeInteger(intended.expected_revision) || intended.expected_revision < 0)) {
    throw new FormationApiError('FORMATION_REVISION_REQUIRED', 409, 'FORMATION_REVISION_REQUIRED');
  }
  const response = await formationsRequest(`/api/v1/formations/${encodeURIComponent(idOrKey)}`, {
    method: 'PATCH',
    body: JSON.stringify(intended),
  });
  verifyGuidanceSaved(response, intended, true);
  return response ?? null;
};

export const deleteFormationApi = async (idOrKey: string): Promise<void> => {
  await formationsRequest(`/api/v1/formations/${encodeURIComponent(idOrKey)}`, {
    method: 'DELETE',
  });
};
